import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useAuth, API } from "../App";
import { Loader2, Check, ImageOff } from "lucide-react";
import { handleImgError } from "../lib/imageFallback";

export const ImageLibraryPicker = ({ open, onOpenChange, onSelect, maxSelectable = 10, excludeUrls = [] }) => {
  const { token } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open || !token) return;
    setSelected([]);
    setLoading(true);
    axios
      .get(`${API}/my-images`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setImages(res.data.images || []))
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, [open, token]);

  const visibleImages = images.filter((img) => !excludeUrls.includes(img.url));

  const toggle = (url) => {
    setSelected((prev) => {
      if (prev.includes(url)) return prev.filter((u) => u !== url);
      if (prev.length >= maxSelectable) return prev;
      return [...prev, url];
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="image-library-dialog">
        <DialogHeader>
          <DialogTitle>Reciclar fotos anteriores</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="animate-spin text-[#64748B]" size={28} />
          </div>
        ) : visibleImages.length === 0 ? (
          <div className="py-10 text-center text-[#94A3B8]">
            <ImageOff size={32} className="mx-auto mb-2" />
            <p className="text-sm">No tienes fotos anteriores disponibles</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-[#64748B] -mt-2">
              Toca las fotos que quieras reutilizar ({selected.length} seleccionada{selected.length !== 1 ? "s" : ""}
              {maxSelectable < 99 ? `, máx ${maxSelectable}` : ""})
            </p>
            <div className="grid grid-cols-3 gap-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="image-library-grid">
              {visibleImages.map((img, idx) => {
                const isSelected = selected.includes(img.url);
                return (
                  <button
                    key={`${img.url}-${idx}`}
                    type="button"
                    onClick={() => toggle(img.url)}
                    className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                      isSelected ? "border-emerald-500 ring-2 ring-emerald-200" : "border-[#E2E8F0]"
                    }`}
                    data-testid={`library-image-${idx}`}
                  >
                    <img src={img.url} alt={img.label || `Foto ${idx + 1}`} loading="lazy" onError={handleImgError} className="w-full h-full object-cover" />
                    {isSelected && (
                      <div className="absolute top-1 right-1 bg-emerald-500 text-white rounded-full p-1 shadow-sm">
                        <Check size={12} />
                      </div>
                    )}
                    {img.label && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">
                        {img.label}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700"
                disabled={selected.length === 0}
                onClick={() => onSelect(selected)}
                data-testid="library-use-selected-btn"
              >
                Usar {selected.length > 0 ? `${selected.length} foto${selected.length !== 1 ? "s" : ""}` : "fotos"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImageLibraryPicker;
